import sansVariable from "./fonts/plex-sans-latin-wght.woff2";
import mono400 from "./fonts/plex-mono-latin-400.woff2";
import mono600 from "./fonts/plex-mono-latin-600.woff2";

/**
 * The typefaces, served from the same place as the app.
 *
 * ## Why there is a webfont here at all
 *
 * There was not, and the reason was good: *"a webfont on an internal console
 * is a network request between an operator and their own data, paid on every
 * cold load."* That objection is about **a third party** — a request to
 * `fonts.gstatic.com` on the path between somebody and their own records,
 * which is a dependency and a disclosure and a round trip nobody asked for.
 *
 * These are not that. They are imported, hashed and bundled by Vite, so they
 * come from the same CloudFront distribution as the JavaScript, under the
 * same certificate, cached for a year by content hash. Seventy-six kilobytes
 * once, against a bundle that is already a hundred and fifty.
 *
 * `font-display: swap`, so a cold load renders in the fallback stack
 * immediately and reflows once rather than holding text back. The fallback is
 * a real stack and not a placeholder: a person on the worst connection in the
 * building reads the same screen, in Segoe UI or SF, slightly wider.
 *
 * ## Why IBM Plex
 *
 * The brand note in `theme.js` says teal "reads as instrument rather than as
 * marketing", and that is the whole register of this product — a thing you
 * operate, not a thing you are sold. Plex is drawn for that: it is IBM's
 * corporate face, it has flat terminals and a mechanical rhythm, and it does
 * not read as a startup landing page.
 *
 * It is also deliberately **not Inter**, which is the face every generated
 * interface reaches for and which would make this look like every other
 * console rather than like this one.
 *
 * **One family, two voices.** Plex Sans carries the interface and Plex Mono
 * carries the ids — and the ids matter here more than in most products,
 * because a ULID and a Cognito client id are read character by character.
 * Plex Mono has a slashed zero and unambiguous `1lI`, which is the reason to
 * pick a mono rather than settle for whatever the machine has.
 *
 * The sans is a **variable** font on the weight axis: one 45 KB file covers
 * every weight this interface uses, from the 400 of body text to the 600 of a
 * heading to the 300 of a display number, with no extra request per weight.
 * The mono is two static cuts because only two are used and two small statics
 * beat one larger variable.
 */
const fontFace = `
  @font-face {
    font-family: "Plex Sans";
    font-style: normal;
    /* The whole axis, in one file. */
    font-weight: 100 700;
    font-display: swap;
    src: url(${sansVariable}) format("woff2-variations");
  }
  @font-face {
    font-family: "Plex Mono";
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url(${mono400}) format("woff2");
  }
  @font-face {
    font-family: "Plex Mono";
    font-style: normal;
    font-weight: 600;
    font-display: swap;
    src: url(${mono600}) format("woff2");
  }
`;

/**
 * The stacks, with the fallback spelled out.
 *
 * Every one of these ends in the system stack that used to be the whole
 * answer. A font that fails to load, or a browser that refuses the variations
 * format, gets exactly the interface this product shipped with — which is the
 * point of a fallback, and is why it is a real stack rather than `sans-serif`.
 */
export const SANS =
  '"Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

export const MONO =
  '"Plex Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

export default fontFace;
